export const notSetUpMessage = "Payments aren't set up yet.";
const endpoints = new Set(['work-invoices', 'issue-work-invoice', 'create-job-payment', 'refresh-job-payment', 'refund-job-payment',
  'supplier-payment-status', 'save-supplier-invoice-profile', 'create-supplier-onboarding', 'cash-payment']);

/** Calls one payment function as the signed-in user. A reply that lands after the account changed is dropped. */
export async function requestPayments(client, endpoint, body, fetcher = globalThis.fetch) {
  if (!endpoints.has(endpoint)) throw new Error('Unknown payment operation.');
  if (!client) throw Object.assign(new Error(notSetUpMessage), { code: 'PAYMENTS_NOT_CONFIGURED' });
  const { data, error } = await client.auth.getSession();
  if (error || !data?.session) throw Object.assign(new Error('Sign in to continue.'), { code: 'AUTH_REQUIRED' });
  const userId = data.session.user.id;
  let response;
  try {
    response = await fetcher(`/.netlify/functions/${endpoint}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` }, body: JSON.stringify(body),
    });
  } catch {
    throw Object.assign(new Error('Could not reach Resident Service 360. Check your connection and try again.'), { code: 'NETWORK' });
  }
  const result = await response.json().catch(() => ({}));
  const current = await client.auth.getSession();
  if (current.data?.session?.user.id !== userId) throw Object.assign(new Error('Your account changed. Please try again.'), { code: 'AUTH_CHANGED' });
  if (!response.ok) {
    const notSetUp = result.code === 'PAYMENTS_NOT_CONFIGURED' || response.status === 404;
    throw Object.assign(new Error(notSetUp ? notSetUpMessage : result.error || 'Payments are temporarily unavailable. Please try again.'),
      { code: notSetUp ? 'PAYMENTS_NOT_CONFIGURED' : result.code || 'PAYMENTS_UNAVAILABLE' });
  }
  return result;
}
export const isNotSetUp = (error) => error?.code === 'PAYMENTS_NOT_CONFIGURED';

/** Only Stripe-hosted HTTPS pages are ever opened from a payment response. */
export function stripeHostedUrl(value, hosts) {
  let url;
  try { url = new URL(value); } catch { throw new Error('Invalid payment link.'); }
  if (url.protocol !== 'https:' || !hosts.includes(url.hostname) || url.username || url.password) throw new Error('Invalid payment link.');
  return url.href;
}
export function safeReceiptUrl(value) {
  try { const url = stripeHostedUrl(value, ['pay.stripe.com']); return url.startsWith('https://pay.stripe.com/receipts/') ? url : null; } catch { return null; }
}
export function formatMoney(amountMinor, currency) {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amountMinor / 100); }
  catch { return `${(amountMinor / 100).toFixed(2)} ${currency}`; }
}
export function parseAmount(value) {
  const match = /^\s*(\d{1,9})(?:\.(\d{1,2}))?\s*$/.exec(String(value).replace(/[£$€,]/g, ''));
  if (!match) return null;
  const minor = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return minor > 0 ? minor : null;
}
const cashErrors = {
  INVOICE_ALREADY_PAID: 'This has already been paid.',
  PAYMENT_IN_PROGRESS: 'A card payment has already started. Please wait a moment, then check again.',
  NO_CASH_OFFER: 'The customer is no longer paying in cash. Please reload to see the latest.',
  FORBIDDEN: "You can't change how this invoice is paid.",
  INVOICE_NOT_FOUND_OR_FORBIDDEN: "You can't change how this invoice is paid.",
  NOT_FOUND: "We couldn't find this invoice. Please reload.",
};
/** Offer ('offer'), withdraw, decline or confirm a cash payment. Resolves to the updated invoice; errors are plain English. */
export async function cashPayment(client, invoiceId, action, fetcher = globalThis.fetch) {
  try {
    return (await requestPayments(client, 'cash-payment', { invoiceId, action }, fetcher)).invoice;
  } catch (cause) {
    if (cashErrors[cause?.code]) throw Object.assign(new Error(cashErrors[cause.code]), { code: cause.code });
    throw cause;
  }
}
const labels = { paid: 'Paid', pending: 'Payment in progress', partially_refunded: 'Partly refunded', refunded: 'Refunded', disputed: 'Disputed', unpaid: 'Not paid' };
export function statusLabel(status, invoice) {
  if (status === 'paid' && invoice?.paymentMethod === 'cash') return 'Paid in cash';
  if ((!status || status === 'unpaid') && invoice?.cashOfferedAt) return 'Paying in cash';
  return labels[status] || 'Not paid';
}
/** A UK date like "12 March", with the year added when it isn't this year. */
export function formatPaidDate(value, now = new Date()) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }), timeZone: 'Europe/London' });
}
export const settled = (status) => ['paid', 'partially_refunded', 'refunded', 'disputed'].includes(status);
const payoutLabels = { paid: 'Paid out', pending: 'Pending', in_transit: 'On the way', canceled: 'Cancelled', failed: 'Failed' };
export const payoutLabel = (status) => payoutLabels[status] || 'Pending';
